import { z } from 'zod'
import { optionalText } from './fields'
import {
  supplierCategorySchema,
  supplierProfileSchema,
  supplierServiceAreaSchema,
  updateSupplierProfileSchema,
} from './supplier'

export const CATALOG_SUPPLIER_SOURCES = ['seed', 'admin', 'agent', 'planner'] as const
export const CATALOG_CORRECTION_STATUSES = ['open', 'resolved'] as const
export const CATALOG_RECORD_STATUSES = ['active', 'archived', 'all'] as const
export const CATALOG_SUPPLIER_IMAGE_LIMIT = 20

export const createCatalogSupplierSchema = supplierProfileSchema
export const updateCatalogSupplierSchema = updateSupplierProfileSchema

export const catalogSupplierListQuerySchema = z.object({
  search: optionalText(120),
  serviceArea: supplierServiceAreaSchema.optional(),
  category: supplierCategorySchema.optional(),
  status: z.enum(CATALOG_RECORD_STATUSES).default('active'),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
})

export const createCatalogCorrectionSchema = z.object({
  message: z.string().trim().min(1).max(2000),
})

export const catalogCorrectionListQuerySchema = z.object({
  status: z.enum(CATALOG_CORRECTION_STATUSES).default('open'),
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
})

/** Every wedding in the name group is relinked; there is no wedding selection. */
export const promoteCustomSuppliersSchema = z.object({
  groupKey: z.string().min(1).max(200),
  supplier: createCatalogSupplierSchema,
})

/** Attaches an uploaded, completed media image to the end of a supplier's gallery. */
export const attachCatalogSupplierImageSchema = z.object({ assetId: z.uuid() })

/** The supplier's full gallery in its new order; the first image becomes the cover. */
export const reorderCatalogSupplierImagesSchema = z.object({
  imageIds: z
    .array(z.uuid())
    .max(CATALOG_SUPPLIER_IMAGE_LIMIT)
    .refine((ids) => new Set(ids).size === ids.length),
})

export type CatalogSupplierSource = (typeof CATALOG_SUPPLIER_SOURCES)[number]
export type CatalogRecordStatus = (typeof CATALOG_RECORD_STATUSES)[number]
export type CreateCatalogSupplierInput = z.infer<typeof createCatalogSupplierSchema>
export type UpdateCatalogSupplierInput = z.infer<typeof updateCatalogSupplierSchema>
export type CatalogSupplierListQuery = z.infer<typeof catalogSupplierListQuerySchema>
export type CatalogCorrectionStatus = (typeof CATALOG_CORRECTION_STATUSES)[number]
export type CreateCatalogCorrectionInput = z.infer<typeof createCatalogCorrectionSchema>
export type CatalogCorrectionListQuery = z.infer<typeof catalogCorrectionListQuerySchema>
export type PromoteCustomSuppliersInput = z.infer<typeof promoteCustomSuppliersSchema>
export type AttachCatalogSupplierImageInput = z.infer<typeof attachCatalogSupplierImageSchema>
export type ReorderCatalogSupplierImagesInput = z.infer<typeof reorderCatalogSupplierImagesSchema>
